import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Scale, Plus, Trash2 } from 'lucide-react';
import { isoDate } from '../../utils/time';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Field, FormRow, fmtDate, money } from './design';

// Budgets checked against what Services Australia shows in the Aged Care Provider Portal (its
// budget view), until the budget API can be used: the quarterly budget with each named supplement,
// a pathway or AT-HM entitlement, or the unspent HCP funds it holds. A difference of a dollar or
// more waits for someone to take Services Australia's figure or say why it stands.
const INP = 'input-field';
const blank = () => ({ clientId: '', budgetType: 'ON', periodStart: isoDate(), asAt: isoDate(), saAmount: '', saBalance: '', supplements: [], note: '' });
const diffText = (d) => (d == null ? '—' : `${d > 0 ? '+' : ''}${money(d)}`);

// What taking Services Australia's figure changes, said before it is done because it overwrites
// Rostery's own budget. Follows what the server changes for each kind of budget.
function takeEffect(k) {
  const amount = k.sa_amount != null ? money(k.sa_amount) : null;
  const left = k.sa_balance != null ? money(k.sa_balance) : null;
  if (k.budget_type === 'ON') {
    const budget = amount ? `Rostery's quarterly budget for that quarter becomes ${amount} (it is added if there is none)` : null;
    if (budget && left) return `${budget}, and its carryover is changed so what is left matches ${left}.`;
    if (budget) return `${budget}.`;
    if (left) return `The carryover on Rostery's quarterly budget for that quarter is changed so what is left matches ${left}.`;
  }
  if ((k.budget_type === 'EL' || k.budget_type === 'RC') && amount) return `Rostery's pathway budget becomes ${amount}.`;
  if ((k.budget_type === 'AT' || k.budget_type === 'HM') && amount) return `Rostery's AT-HM entitlement becomes ${amount}.`;
  if (k.budget_type === 'HCP' && left) return `The unspent HCP funds Rostery shows Services Australia holding become ${left}.`;
  return "Rostery's figure for this budget is replaced with Services Australia's.";
}

export default function SaBudgetChecks({ clients, clientId }) {
  const [data, setData] = useState({ rows: [], types: {} });
  // A failed load says so: an empty list would read as "no checks recorded yet".
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [ours, setOurs] = useState(null);
  const [why, setWhy] = useState({});
  const confirm = useConfirm();
  const load = useCallback(() => {
    api.get('/aged-care/sa-budget-checks', { params: clientId ? { client_id: clientId } : {} })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => { setData((d) => ({ rows: [], types: d.types })); setError(e.response?.data?.message || 'Could not load the checks'); });
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  // Rostery's figure for the same budget, as the participant, budget and date are chosen.
  const fClient = form ? form.clientId : ''; const fType = form ? form.budgetType : ''; const fDate = form ? form.periodStart : '';
  useEffect(() => {
    if (!fClient || !fType || !fDate) { setOurs(null); return; }
    api.get('/aged-care/sa-budget-checks/ours', { params: { client_id: fClient, type: fType, date: fDate } })
      .then((r) => setOurs(r.data)).catch(() => setOurs(null));
  }, [fClient, fType, fDate]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const r = await api.post('/aged-care/sa-budget-checks', { ...form, supplements: form.supplements.filter((s) => s.name && Number(s.amount) > 0) });
      toast.success(r.data.differs ? 'Saved. It differs from Rostery: take Services Australia\'s figure or say why it stands.' : 'Saved. It matches Rostery.');
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the check'); }
  };
  const apply = async (k) => {
    const ok = await confirm(takeEffect(k), { title: "Take Services Australia's figure?", confirmLabel: "Take Services Australia's figure" });
    if (!ok) return;
    try { const r = await api.post(`/aged-care/sa-budget-checks/${k.id}/apply`); toast.success(`Updated: ${(r.data.done || []).join(', ')}`); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not take the figure'); }
  };
  const reviewed = async (k) => {
    try { await api.post(`/aged-care/sa-budget-checks/${k.id}/reviewed`, { note: why[k.id] || '' }); setWhy((w) => ({ ...w, [k.id]: '' })); toast.success('Saved'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save it'); }
  };
  const T = data.types || {};
  return (
    <Panel title="Checked against Services Australia" icon={Scale} className="mb-5" bodyClass="divide-y divide-slate-100"
      actions={!form && <button type="button" className="btn-secondary btn-sm" onClick={() => { setForm({ ...blank(), clientId: clientId || '' }); }}><Plus size={13} /> Record a check</button>}>
      <p className="px-4 py-2.5 text-[12px] text-slate-500">
        Read each budget from the Aged Care Provider Portal (supplements included) and record it here. A difference of a dollar or more is flagged until you take
        Services Australia's figure or say why it stands. After an End-of-Life Pathway ends, record the ongoing balance the portal shows.
      </p>
      {form && (
        <div className="px-4 py-3 space-y-3">
          <FormRow cols={3}>
            <Field label="Participant">{(id) => (
              <select id={id} className={INP} value={form.clientId} onChange={(e) => set('clientId', e.target.value)}>
                <option value="">Choose</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}</Field>
            <Field label="Budget">{(id) => (
              <select id={id} className={INP} value={form.budgetType} onChange={(e) => set('budgetType', e.target.value)}>
                {Object.entries(T).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            )}</Field>
            <Field label="For" hint="A day in the quarter, or the episode or entitlement start.">{(id) => <input id={id} type="date" className={INP} value={form.periodStart} onChange={(e) => set('periodStart', e.target.value)} />}</Field>
          </FormRow>
          <FormRow cols={3}>
            <Field label="Services Australia's budget ($)">{(id) => <input id={id} type="number" step="0.01" className={INP} value={form.saAmount} onChange={(e) => set('saAmount', e.target.value)} />}</Field>
            <Field label="What it shows as left ($)">{(id) => <input id={id} type="number" step="0.01" className={INP} value={form.saBalance} onChange={(e) => set('saBalance', e.target.value)} />}</Field>
            <Field label="Read on">{(id) => <input id={id} type="date" className={INP} value={form.asAt} onChange={(e) => set('asAt', e.target.value)} />}</Field>
          </FormRow>
          {ours && (
            <p className="text-[12px] text-slate-600">
              Rostery: {ours.amount != null ? `budget ${money(ours.amount)}` : 'no budget figure'}{ours.balance != null ? `, left ${money(ours.balance)}` : ''}
              {ours.claimed != null ? ` (after ${money(ours.claimed)} claimed)` : ''}{ours.note ? `. ${ours.note}` : ''}
            </p>
          )}
          {form.budgetType === 'ON' && (
            <div className="space-y-1.5">
              <p className="text-[12.5px] font-medium text-slate-700">Supplements as the portal names them</p>
              {form.supplements.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input aria-label="Supplement" className={`${INP} !w-56`} value={s.name} placeholder="e.g. Oxygen supplement"
                    onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, supplements: f.supplements.map((x, j) => (j === i ? { ...x, name: v } : x)) })); }} />
                  <input aria-label="Amount this quarter" type="number" step="0.01" className={`${INP} !w-32`} value={s.amount}
                    onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, supplements: f.supplements.map((x, j) => (j === i ? { ...x, amount: v } : x)) })); }} />
                  <button type="button" className="btn-ghost btn-sm" aria-label="Remove" onClick={() => setForm((f) => ({ ...f, supplements: f.supplements.filter((_, j) => j !== i) }))}><Trash2 size={13} /></button>
                </div>
              ))}
              <button type="button" className="btn-ghost btn-sm" onClick={() => setForm((f) => ({ ...f, supplements: [...f.supplements, { name: '', amount: '' }] }))}><Plus size={13} /> Add a supplement</button>
            </div>
          )}
          <Field label="Note">{(id) => <input id={id} className={INP} value={form.note} onChange={(e) => set('note', e.target.value)} />}</Field>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary btn-sm" onClick={() => setForm(null)}>Cancel</button>
            <button type="button" className="btn-primary btn-sm" disabled={!form.clientId} onClick={save}>Save</button>
          </div>
        </div>
      )}
      {error && (
        <div className="px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px]" role="alert">
          <span className="text-red-700">{error}</span>
          <button type="button" className="btn-secondary btn-sm" onClick={load}>Try again</button>
        </div>
      )}
      {!error && !data.rows.length && !form && <p className="px-4 py-3 text-[13px] text-slate-500">No checks recorded yet.</p>}
      {data.rows.slice(0, 30).map((k) => (
        <div key={k.id} className="px-4 py-2.5 text-[13px]">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-slate-800">
              <b>{k.participant}</b> · {T[k.budget_type] || 'Budget'} · for {fmtDate(k.period_start)} · read {fmtDate(k.as_at)}
            </span>
            <span className={k.differs && !k.applied && !k.reviewed_at ? 'text-amber-800' : 'text-slate-500'}>
              {k.applied ? "Services Australia's figure taken" : k.differs ? (k.reviewed_at ? 'Difference explained' : 'Differs') : 'Matches'}
            </span>
          </div>
          <p className="text-[12px] text-slate-600">
            Budget: Services Australia {k.sa_amount != null ? money(k.sa_amount) : '—'}, Rostery {k.ours_amount != null ? money(k.ours_amount) : '—'} ({diffText(k.amount_diff != null ? Number(k.amount_diff) : null)}).
            {' '}Left: Services Australia {k.sa_balance != null ? money(k.sa_balance) : '—'}, Rostery {k.ours_balance != null ? money(k.ours_balance) : '—'} ({diffText(k.balance_diff != null ? Number(k.balance_diff) : null)}).
            {Array.isArray(k.supplements) && k.supplements.length ? ` Supplements: ${k.supplements.map((x) => `${x.name} ${money(x.amount)}`).join(', ')}.` : ''}
            {k.note ? ` ${k.note}` : ''}
          </p>
          {k.differs && !k.applied && !k.reviewed_at && (
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <button type="button" className="btn-primary btn-sm" onClick={() => apply(k)}>Take Services Australia's figure</button>
              <input aria-label="Why the difference stands" className={`${INP} !w-64 !py-1 !text-[12px]`} placeholder="Or say why it stands" value={why[k.id] || ''}
                onChange={(e) => { const v = e.target.value; setWhy((w) => ({ ...w, [k.id]: v })); }} />
              <button type="button" className="btn-secondary btn-sm" disabled={!(why[k.id] || '').trim()} onClick={() => reviewed(k)}>Save</button>
            </div>
          )}
        </div>
      ))}
    </Panel>
  );
}
